import Tabs from "@mui/material/Tabs";
import Tab, { TabProps } from "@mui/material/Tab";
import { Box } from "@mui/material";
import { ReactElement } from "react";
import Loading from "../Loading/Loading";
import { typography } from "../../utils/typography";

type TabsComponentProps = {
  value: number;
  handleChange: (value: number) => void;
  tabs: ({ contentBody: ReactElement } & TabProps)[];
  loading?: boolean;
  /**
   * Rendered next to the tabs, inside the sticky header — for a control that
   * applies to every tab and has to stay reachable while scrolling.
   */
  toolbar?: ReactElement;
};
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

export default function TabsComponent({
  value,
  handleChange,
  tabs,
  loading = false,
  toolbar
}: TabsComponentProps) {
  const handleTab = (_: any, value: number) => handleChange(value);

  return (
    <Box sx={{ bgcolor: "#FCFCFF", minHeight: "100vh" }}>
      <Box
        sx={{
          bgcolor: "#FCFCFF",
          boxShadow: "0px 4px 6px rgba(30, 39, 79, 0.1)",
          position: "sticky",
          top: 80,
          zIndex: 10,
          // The toolbar drops under the tabs on narrow screens rather than
          // squeezing them, since the tab strip already scrolls horizontally.
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 1
        }}
      >
        <Tabs
          value={value}
          onChange={handleTab}
          aria-label="payments tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#0DBDA7",
              height: "2px"
            },
            minHeight: 47,
            px: { xs: "16px", sm: "24px" }
          }}
        >
          {tabs.map((tab, i) => (
            <Tab
              key={i}
              value={i}
              label={tab.label}
              sx={{
                minHeight: 47,
                ...typography.body.m,
                fontWeight: value === i ? 600 : 400,
                color: value === i ? "#0DBDA7" : "#1E274F",
                textTransform: "none",
                "&.Mui-selected": { color: "#0DBDA7" }
              }}
            />
          ))}
        </Tabs>
        {toolbar && (
          <Box
            sx={{
              px: { xs: "16px", sm: "24px" },
              // Vertical room for the floating label of a select, which sits
              // above the field and would otherwise be cut by the header edge.
              py: 1,
              flexShrink: 0
            }}
          >
            {toolbar}
          </Box>
        )}
      </Box>
      {loading ? (
        <Box
          alignItems={"center"}
          justifyContent={"center"}
          display={"flex"}
          marginTop={10}
        >
          <Loading />
        </Box>
      ) : (
        tabs.map((tab, i) => (
          <TabPanel key={i} value={value} index={i}>
            {tab.contentBody}
          </TabPanel>
        ))
      )}
    </Box>
  );
}
